import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FileLink } from 'apps/repository/views/fileLink';
import { clientStore } from 'apps/client/app';
import {
  FileInputWrapper,
  InvisibleInput,
  ActionsColumn,
} from 'apps/repository/styles';
import { Button, Tooltip } from 'components';
import { withNamespaces, Trans } from 'react-i18next';

const actionInfo = (fileInfo, content, t) => {
  const repoManager = clientStore.context.get('repoAppManager');
  const { reference } = fileInfo;
  return {
    download: {
      getTooltip: fileType => t(`Download this ${fileType}`),
      children: (
        <FileLink fileInfo={fileInfo} content={content}>
          <Button
            variant='secondary'
            icon='download'
            data-test-id='project-download-file'
            ariaLabel='Download file'
            size='xsmall'
            outline
          />
        </FileLink>
      ),
    },
    reset: {
      getTooltip: () => t('Reset changes'),
      children: (
        <Button
          variant='secondary'
          icon='undo'
          ariaLabel='Reset changes'
          onClick={() => repoManager.ResetFile(fileInfo)}
          size='xsmall'
          outline
        />
      ),
    },
    delete: {
      getTooltip: fileType => t(`Delete this ${fileType}`),
      children: (
        <Button
          variant='secondary'
          icon='delete'
          data-test-id='project-delete-file'
          ariaLabel='Delete file'
          onClick={() => repoManager.DeleteFile(fileInfo)}
          size='xsmall'
          outline
        />
      ),
    },
    rename: {
      getTooltip: fileType => t(`Rename this ${fileType}`),
      children: (
        <Button
          variant='secondary'
          icon='edit'
          ariaLabel='Rename file'
          data-test-id='project-rename-file'
          onClick={() => reference.SetRenaming(true)}
          size='xsmall'
          outline
        />
      ),
    },
    replace: {
      getTooltip: fileType => t(`Replace this ${fileType}`),
      children: (
        <FileInputWrapper>
          <Button variant='secondary' icon='loop' size='xsmall' outline />
          <InvisibleInput size={60} type='file' />
        </FileInputWrapper>
      ),
    },
  };
};

@withNamespaces()
@observer
export class Actions extends React.Component {
  render() {
    const { fileInfo, canContribute, project, t } = this.props;
    const { reference = {} } = fileInfo;
    const fileType = reference.isFolder ? 'folder' : 'file';

    if (reference && reference.renaming) {
      const repoManager = clientStore.context.get('repoAppManager');
      return (
        <ActionsColumn renaming={reference.renaming} isFlex>
          <ButtonStyled
            data-test-id='project-cancel-rename-file'
            text={<Trans>Cancel</Trans>}
            size='xsmall'
            variant='secondary'
            onClick={() => reference.SetRenaming(false)}
            outline
          />
          <ButtonStyled
            data-test-id='project-confirm-rename-file'
            text={<Trans>Rename</Trans>}
            size='xsmall'
            onClick={() =>
              repoManager.RenameFile(fileInfo, reference.newFilename)
            }
          />
        </ActionsColumn>
      );
    }

    const availableActions = [];

    if (reference) {
      availableActions.push('download');
      if (
        canContribute &&
        !(reference.opType && reference.opType === 'REMOVE')
      ) {
        availableActions.push('delete', 'rename');
      }
    }
    if (canContribute && reference.opType) {
      availableActions.unshift('reset');
    }

    return (
      <ActionsColumn isFlex isFlexEnd>
        {availableActions.map((action, i) => {
          const info = actionInfo(fileInfo, project.content, t)[action];
          const { children } = info;
          return (
            <React.Fragment key={i}>
              <Tooltip delayShow={500} />
              <Wrapper
                key={i}
                data-tip={info.getTooltip(fileType)}
                hidden={action === 'download' && fileType === 'folder'}
              >
                {children}
              </Wrapper>
            </React.Fragment>
          );
        })}
      </ActionsColumn>
    );
  }
}

const Wrapper = styled.div`
  margin-left: ${props => props.marginLeft || '15px'};
  z-index: ${props => props.zIndex || '2'};
  display: ${props => props.hidden && 'none'};
  float: right;
`;

const ButtonStyled = styled(Button)`
  margin-left: ${props => props.theme.spacing.small};
`;

Actions.propTypes = {
  fileInfo: PropTypes.object,
  project: PropTypes.object,
  canContribute: PropTypes.bool,
  t: PropTypes.func,
};
